import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Users, Activity, Crosshair, Layers3, BookOpen, Sword, MonitorSmartphone, Mountain, Compass, ChevronDown, Skull, Shield, Flame, Play, Maximize2, X } from 'lucide-react';

const MAP_MAIN = 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/6b47ca14_Screenshot%202026-05-06%20191829.png';
const MAP_TUTORIAL = 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/5dgo7cmx_Screenshot%202026-05-06%20191857.png';

const metrics = [
  { icon: Users, label: 'Active Units', value: '128', sub: '+12 dispatched' },
  { icon: Activity, label: 'Tick Rate', value: '60Hz', sub: 'Stable · 0 drops' },
  { icon: Crosshair, label: 'Threats', value: '03', sub: 'NW Quadrant' },
  { icon: Layers3, label: 'Build Queue', value: '07', sub: '2 bottlenecked' },
];

const mechanics = [
  {
    icon: Layers3,
    title: 'Economy & Progression',
    body: 'Resource flow, tech and reinforcement loops that turn small early advantages into late-game pressure.',
    items: ['Scalability', 'Technologies', 'Reinforcements'],
  },
  {
    icon: Sword,
    title: 'Combat & Positioning',
    body: 'Tactile, formation-aware unit control — designed so the player feels their hand on every squad.',
    items: ['Square & Triangle Formation', 'Drag & Drop Movement', 'Select Unit / Building'],
  },
  {
    icon: MonitorSmartphone,
    title: 'UI / UX & Controls',
    body: 'Player-first viewports and a grounded 3D world that keeps macro information one glance away.',
    items: ['Graphics & Viewpoint Settings', '3D Realistic Environment', 'Building & Resources Viewport'],
  },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.8 } } };

const accordions = [
  { id: 'trailer', label: 'Gameplay Trailer', tease: 'A 60-second look at The Crimson Dirge in motion.', icon: Play },
  { id: 'lore', label: 'Lore Deep-Dive', tease: 'How sorrow became doctrine — and a berserker became a banner.', icon: BookOpen },
  { id: 'maps', label: 'Maps', tease: 'Two hand-shaped Unreal landscapes: a tutorial hollow and the Vale of the Cinder Crown.', icon: Mountain },
  { id: 'mechanics', label: 'Mechanics', tease: 'Economy, combat positioning, and player-first UI / UX.', icon: Sword },
  { id: 'ui', label: 'Custom UI', tease: 'A live-command HUD authored from scratch in UMG.', icon: MonitorSmartphone },
];

export default function RTSSection() {
  const [open, setOpen] = useState(null);
  const [topOpen, setTopOpen] = useState(null);

  return (
    <section id="rts" className="relative py-24 lg:py-32" data-testid="rts-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="flex items-center gap-4 mb-10">
          <Cpu className="text-[#e58a59]" size={20} />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-400">Chapter 02 — RTS · RPG Campaign</span>
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-zinc-500">Unreal Engine</span>
        </motion.div>

        {/* Title */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-4 text-xs uppercase tracking-[0.3em] text-[#e58a59]" data-testid="rts-eyebrow">A Real-Time Strategy with an RPG campaign · Working title</motion.div>
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-black text-white text-6xl sm:text-7xl lg:text-[9rem] leading-[0.95] tracking-tighter mb-8" data-testid="rts-title">
          The Crimson<br /><span className="italic font-light text-[#e58a59]">Dirge.</span>
        </motion.h2>

        {/* Logline */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-white/10 bg-[#101012] p-8 lg:p-12 mb-16 relative overflow-hidden" data-testid="rts-logline">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#e58a59' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <Flame size={16} className="text-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Logline — Story Spine</span>
            </div>
            <p className="font-display font-light text-white text-2xl lg:text-4xl leading-[1.25] max-w-4xl">
              For thousands of years, the war has refused to end. Two tribes — <span className="text-[#e58a59]">Humanity</span> and the <span className="text-[#e58a59]">Demon Kingdom</span> — fight not just for territory, but for the right to exist.
            </p>
            <p className="text-zinc-400 text-base lg:text-lg leading-relaxed mt-6 max-w-3xl">
              Each believes they are the victim. Each believes they are justified. And every death on both sides is called honor.
            </p>
          </div>
        </motion.div>

        {/* Credits & Context */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="rts-credits"
        >
          <div className="p-6 rounded-xl border border-white/10 bg-[#101012]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] font-semibold">Context</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed">
              This prototype was built as my <span className="text-white font-semibold">Master&rsquo;s degree project</span> — a first-ever game where I could stress-test elements, mechanics, style and art all at once. It&rsquo;s the piece that taught me how to ship, not just design.
            </p>
          </div>
          <div className="p-6 rounded-xl border border-white/10 bg-[#101012]">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] font-semibold">Built With · Following</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-3">
              Authored end-to-end in <span className="text-white font-semibold">Unreal Blueprints</span>, learning-as-I-built alongside YouTube tutorial series from:
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'UNF Games', href: 'https://www.youtube.com/@UNFGames' },
                { name: 'TwoNeurons', href: 'https://www.youtube.com/@TwoNeurons' },
                { name: 'BuildGamesWithJon', href: 'https://www.youtube.com/@buildgameswithjon7641' },
              ].map((c) => (
                <a
                  key={c.name}
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-[#1a1a1c] text-xs text-zinc-200 hover:border-[#e58a59]/50 hover:text-[#e58a59] transition-colors"
                >
                  {c.name}
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Top-level collapsibles — save space by collapsing entire subsections */}
        <div className="space-y-3" data-testid="rts-toplevel">
          {/* Kingdoms tab */}
          <div className="rounded-xl border border-white/10 bg-[#101012] overflow-hidden glow-card" data-testid="rts-top-kingdoms">
            <button
              onClick={() => setTopOpen(topOpen === 'kingdoms' ? null : 'kingdoms')}
              className="w-full flex items-center gap-4 p-5 lg:p-6 text-left hover:bg-white/[0.02] transition-colors"
              data-testid="rts-top-toggle-kingdoms"
            >
              <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center bg-[#1a1a1c] shrink-0">
                <Shield size={16} className="text-[#e58a59]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg lg:text-xl text-white font-bold">The Two Kingdoms</div>
                <div className="text-xs lg:text-sm text-zinc-400 mt-0.5 truncate">Weeping Cinder vs. Aegis of Ashes — the war of two right sides.</div>
              </div>
              <ChevronDown size={18} className={`text-zinc-400 transition-transform shrink-0 ${topOpen === 'kingdoms' ? 'rotate-180 text-[#e58a59]' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
              {topOpen === 'kingdoms' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 lg:px-6 pb-8 pt-2 border-t border-white/5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4" data-testid="rts-kingdoms">
                      <KingdomCard
                        accent="#cf3a3a"
                        tag="Demon Kingdom"
                        name="The Weeping Cinder"
                        icon={Skull}
                        quote="We did not ask for this war. But we will be the ones to finish it."
                        champion="Kaelthar, the Blood-Weep"
                        banner="Retaliation before extinction."
                        body="For centuries, the demons of the Weeping Cinder lived in fractured peace — orcs, imps, fallen beasts and shadow-touched beings, bound by a single understanding: difference was not weakness. Then came the human crusades. Villages burned for cleansing. Children impaled as warnings. Among the slaughtered was Kaelthar&rsquo;s entire clan."
                        playstyle="Chaotic, berserker-focused melee. Units grow stronger as they take damage. Economy is unstable but snowballs when fed enemy corpses."
                        testId="kingdom-weeping-cinder"
                      />
                      <KingdomCard
                        accent="#d4a86a"
                        tag="Human Kingdom"
                        name="Aegis of Ashes"
                        icon={Shield}
                        quote="The monsters began this war. We will end it."
                        champion="—"
                        banner="Liberation through fire."
                        body="Humanity remembers the Crimson Night — 800 years ago, demon war bands poured through a rift and slaughtered three kingdoms in a single evening. Every child is raised on the story. To Aegis, demons are not people; they are things. When their armies burn demon villages, they call it liberation. When they kill demon children, they call it pruning a poisoned branch."
                        playstyle="Defensive, formation-based, tech-focused. Strong economy and fortified positions. Slower but more deliberate than the chaotic demons."
                        testId="kingdom-aegis-of-ashes"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Deep Dive tab — nests the 5 sub-accordions inside */}
          <div className="rounded-xl border border-white/10 bg-[#101012] overflow-hidden glow-card" data-testid="rts-top-deepdive">
            <button
              onClick={() => setTopOpen(topOpen === 'deepdive' ? null : 'deepdive')}
              className="w-full flex items-center gap-4 p-5 lg:p-6 text-left hover:bg-white/[0.02] transition-colors"
              data-testid="rts-top-toggle-deepdive"
            >
              <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center bg-[#1a1a1c] shrink-0">
                <BookOpen size={16} className="text-[#e58a59]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg lg:text-xl text-white font-bold">Deep Dive</div>
                <div className="text-xs lg:text-sm text-zinc-400 mt-0.5 truncate">Trailer · Lore · Maps · Mechanics · Custom UI</div>
              </div>
              <ChevronDown size={18} className={`text-zinc-400 transition-transform shrink-0 ${topOpen === 'deepdive' ? 'rotate-180 text-[#e58a59]' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
              {topOpen === 'deepdive' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 lg:px-6 pb-6 pt-4 border-t border-white/5">
                    {/* Nested sub-accordions */}
                    <div className="space-y-3" data-testid="rts-accordions">
                      {accordions.map((a) => {
                        const Icon = a.icon;
                        const isOpen = open === a.id;
                        return (
                          <div key={a.id} className="rounded-lg border border-white/10 bg-[#0e0e10] overflow-hidden" data-testid={`rts-acc-${a.id}`}>
                            <button onClick={() => setOpen(isOpen ? null : a.id)} className="w-full flex items-center gap-4 p-4 lg:p-5 text-left hover:bg-white/[0.02] transition-colors" data-testid={`rts-acc-toggle-${a.id}`}>
                              <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center bg-[#1a1a1c] shrink-0">
                                <Icon size={14} className="text-[#e58a59]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-display text-base lg:text-lg text-white font-bold">{a.label}</div>
                                <div className="text-xs text-zinc-400 mt-0.5 truncate">{a.tease}</div>
                              </div>
                              <ChevronDown size={16} className={`text-zinc-400 transition-transform shrink-0 ${isOpen ? 'rotate-180 text-[#e58a59]' : ''}`} />
                            </button>
                            <AnimatePresence initial={false}>
                              {isOpen && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }} className="overflow-hidden">
                                  <div className="px-4 lg:px-5 pb-6 pt-2 border-t border-white/5">
                                    {a.id === 'trailer' && <TrailerContent />}
                                    {a.id === 'lore' && <LoreContent />}
                                    {a.id === 'maps' && <MapsContent />}
                                    {a.id === 'mechanics' && <MechanicsContent />}
                                    {a.id === 'ui' && <UIContent />}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function KingdomCard({ accent, tag, name, icon: Icon, quote, champion, banner, body, playstyle, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#101012] p-7 lg:p-8 glow-card relative overflow-hidden" data-testid={testId}>
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full border flex items-center justify-center" style={{ borderColor: `${accent}66`, background: `${accent}1a` }}>
          <Icon size={16} style={{ color: accent }} />
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{tag}</span>
      </div>
      <h3 className="font-display text-3xl lg:text-4xl text-white font-bold mb-5">{name}</h3>
      <blockquote className="pl-4 border-l-2 italic text-zinc-300 text-sm lg:text-base leading-relaxed mb-5" style={{ borderColor: accent }}>“{quote}”</blockquote>
      <p className="text-zinc-400 text-sm leading-relaxed mb-5">{body}</p>
      <div className="space-y-3 border-t border-white/10 pt-4">
        <Row k="Champion" v={champion} />
        <Row k="Banner" v={banner} />
        <Row k="Playstyle" v={playstyle} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 pt-0.5">{k}</div>
      <div className="col-span-2 text-sm text-white leading-snug">{v}</div>
    </div>
  );
}

function TrailerContent() {
  return (
    <div className="pt-4">
      <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black aspect-video glow-card" data-testid="rts-trailer">
        <iframe
          className="absolute inset-0 w-full h-full"
          src="https://www.youtube-nocookie.com/embed/pXnARq6t0-I?autoplay=1&mute=1&loop=1&playlist=pXnARq6t0-I&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&showinfo=0&iv_load_policy=3"
          title="The Crimson Dirge — Gameplay Trailer"
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen={false}
        />
        {/* Click-blocker overlay — prevents viewers from clicking through to the YouTube page */}
        <div className="absolute inset-0 pointer-events-auto" aria-hidden="true" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/15 pointer-events-none">
          <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59] animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-200">
            Live Trailer · Muted Loop
          </span>
        </div>
      </div>
      <p className="text-zinc-500 text-xs mt-3 italic">
        In-engine footage from The Crimson Dirge — Unreal Engine prototype.
      </p>
    </div>
  );
}

function LoreContent() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
      <div className="lg:col-span-7 space-y-5">
        <p className="text-zinc-300 leading-relaxed">
          Kaelthar survived because he was left for dead beneath a pile of his own family&rsquo;s bodies. When he crawled out, he did not roar. He did not curse. He simply wept — and the tears were blood.
        </p>
        <p className="text-zinc-400 leading-relaxed">
          Now Kaelthar fights not for glory or territory but because silence feels like betrayal. He marches the Weeping Cinder under a single banner: <span className="text-[#e58a59] italic">Retaliation before extinction.</span>
        </p>
        <p className="text-zinc-400 leading-relaxed">
          On the other side, Aegis of Ashes believes — with absolute sincerity — that they are fighting a defensive war. They do not see the weeping orc. They do not hear his story. They only see a monster holding a jagged sword, coming to kill them — just like the stories promised.
        </p>
      </div>
      <div className="lg:col-span-5 p-5 rounded-lg border border-white/10 bg-[#1a1a1c] space-y-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Themes</div>
        {['Two right sides', 'Inherited grief', 'Honor as propaganda', 'Sorrow as fuel'].map((t) => (
          <div key={t} className="flex items-center gap-2 text-sm text-zinc-200"><span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />{t}</div>
        ))}
      </div>
    </div>
  );
}

function MapsContent() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-4">
      <MapCard image={MAP_MAIN} title="Vale of the Cinder Crown" role="Main Battlefield" meta={[{ k: 'Type', v: 'Skirmish' }, { k: 'Biome', v: 'Alpine' }, { k: 'Chokes', v: '4' }]} blurb="A snowbound valley walled by jagged peaks. The forested basin is the only viable battleground, funnelling armies into predictable choke points where Kaelthar's snowball peaks." testId="rts-map-main" />
      <MapCard image={MAP_TUTORIAL} title="The Practice Hollow" role="Tutorial Arena" meta={[{ k: 'Type', v: 'Tutorial' }, { k: 'Biome', v: 'Meadow' }, { k: 'Squads', v: 'Placed' }]} blurb="A controlled hollow ringed by spires. Pre-placed friendly (green) and enemy (red) squads teach selection, movement and engagement before the campaign begins." testId="rts-map-tutorial" />
      <div className="lg:col-span-2 flex items-center gap-2 text-xs text-zinc-500"><Compass size={12} className="text-[#e58a59]" /> Both landscapes shaped and lit by hand in Unreal Engine.</div>
    </div>
  );
}

function MechanicsContent() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
      {mechanics.map((m, i) => (
        <div key={m.title} className="p-6 rounded-lg border border-white/10 bg-[#1a1a1c] flex flex-col" data-testid={`rts-mech-${i}`}>
          <m.icon className="text-[#e58a59] mb-4" size={20} />
          <h4 className="font-display text-lg text-white font-bold mb-2">{m.title}</h4>
          <p className="text-zinc-400 text-sm leading-relaxed mb-4">{m.body}</p>
          <ul className="mt-auto space-y-2 border-t border-white/10 pt-4">
            {m.items.map((it) => (
              <li key={it} className="flex items-start gap-2.5 text-sm text-zinc-200">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e58a59] shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const UI_HOME = 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/3a6uib8m_AnnihilationAge%20Preview%20%5BNetMode_%20Standalone%200%5D%20%20%2864-bit_PC%20D3D%20SM6%29%205_18_2026%208_18_51%20PM.png';
const UI_BATTLE = 'https://customer-assets.emergentagent.com/job_game-design-hub-21/artifacts/otzf3bud_AnnihilationAge%20Preview%20%5BNetMode_%20Standalone%200%5D%20%20%2864-bit_PC%20D3D%20SM6%29%205_18_2026%208_19_49%20PM.png';

const HOME_ANNOTATIONS = [
  { id: 'continue', label: 'Continue', y: 32, note: 'Takes the campaign save state and respawns the player exactly where they left off — respects returning players and is the topmost option on purpose.' },
  { id: 'campaign', label: 'Campaign', y: 38, note: 'Player first picks a difficulty: Easy · Medium · Hard · Expert. Each tier scales soldier counts, battle mechanics and the opponent AI’s advance speed.' },
  { id: 'challenges', label: 'Challenges', y: 45, note: 'Mode-pack of time-based events, elimination goals and extreme scenarios (1 vs 3) — drives achievements, rewards and a leaderboard loop for retention.' },
  { id: 'battle', label: 'Battle Mode', y: 51, note: 'Branches into the dedicated battle-mode hub (local & online sub-modes). The only menu item that opens a second screen instead of a session.' },
  { id: 'tutorial', label: 'Tutorial', y: 57, note: 'State-driven onboarding: camera control, unit formation, building primer, strategy and battle mechanics — completion gates the campaign for new players.' },
  { id: 'settings', label: 'Settings', y: 64, note: 'Resolution and display options today; designed to expand into audio, controls and accessibility without restructuring the menu.' },
  { id: 'exit', label: 'Exit', y: 70, note: 'Anchored to the bottom — standard RTS convention so muscle-memory players never misclick.' },
];

const BATTLE_ANNOTATIONS = [
  { id: 'create-local', label: 'Create Local', x: 22, side: 'right', y: 38, note: 'Initiates the creation of an offline lobby on the host’s machine, discoverable by other devices on the same local network.' },
  { id: 'join-local', label: 'Join Local', x: 22, side: 'right', y: 75, note: 'Finds any open local lobby and joins it. Each player only controls their own units and buildings, but every action syncs live for both sides to see.' },
  { id: 'pvai', label: 'Player vs AI', y: 31, note: 'Spawns the player against AI-controlled bases — outcome and pacing scale with the difficulty the player picks before the match.', inactive: true },
  { id: 'multi', label: 'Multiplayer', y: 53, note: 'Casual online matchmaking with light skill bracketing so new players are matched against opponents at a similar level.', inactive: true },
  { id: 'ranked', label: 'Ranked', y: 75, note: 'Tiered ranking ladder. Performance stats (win rate, APM, objective control) place the player into a category that updates over time.', inactive: true },
];

function AnnotatedScreen({ image, label, title, intro, annotations, anchorX = 22, calloutSide = 'right', calloutOffset = 4, calloutMaxWidth = 280, testId }) {
  const [active, setActive] = useState(null);
  const [fs, setFs] = useState(false);
  const activeAnno = annotations.find((a) => a.id === active) || null;

  useEffect(() => {
    if (!fs) return;
    const onKey = (e) => e.key === 'Escape' && setFs(false);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fs]);

  const calloutLeftStyle =
    calloutSide === 'left'
      ? { right: `${100 - anchorX + calloutOffset}%` }
      : { left: `${anchorX + calloutOffset}%` };

  const canvas = (
    <div
      className={`relative overflow-hidden bg-[#0e0e10] ${fs ? 'w-full h-full' : 'aspect-[16/10]'}`}
      onMouseLeave={() => setActive(null)}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-contain sm:object-cover" />
      <div className={`absolute inset-0 bg-black/10 pointer-events-none transition-opacity duration-300 ${active ? 'opacity-100 bg-black/40' : ''}`} />

      <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#e58a59]">
        {label}
      </div>
      {!fs && (
        <button
          type="button"
          onClick={() => setFs(true)}
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.2em] text-zinc-200 hover:text-[#e58a59] hover:border-[#e58a59]/40 transition-colors"
          data-testid={`${testId}-fs-open`}
          aria-label="Expand to fullscreen"
        >
          <Maximize2 size={12} /> Expand
        </button>
      )}
      {fs && (
        <button
          type="button"
          onClick={() => setFs(false)}
          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:text-[#e58a59] hover:border-[#e58a59]/50 transition-colors"
          data-testid={`${testId}-fs-close`}
          aria-label="Close fullscreen"
        >
          <X size={18} />
        </button>
      )}

      {annotations.map((a, i) => {
        const isActive = active === a.id;
        const pinX = a.x ?? anchorX;
        const pinSide = a.side ?? calloutSide;
        const pinCalloutStyle =
          pinSide === 'left'
            ? { right: `${100 - pinX + calloutOffset}%` }
            : { left: `${pinX + calloutOffset}%` };
        return (
          <React.Fragment key={a.id}>
            <button
              type="button"
              onMouseEnter={() => setActive(a.id)}
              onFocus={() => setActive(a.id)}
              onClick={() => setActive(isActive ? null : a.id)}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
              style={{ left: `${pinX}%`, top: `${a.y}%` }}
              aria-label={`${a.label} — ${a.note}`}
              data-testid={`anno-pin-${a.id}`}
            >
              <span className={`flex items-center justify-center w-7 h-7 rounded-full border text-[11px] font-bold transition-all duration-200 ${
                isActive
                  ? 'bg-[#e58a59] text-black border-[#e58a59] scale-110 shadow-[0_0_18px_rgba(229,138,89,0.45)]'
                  : a.inactive
                    ? 'bg-black/75 text-zinc-400 border-zinc-500/40 hover:border-[#e58a59] hover:text-zinc-200'
                    : 'bg-black/75 text-zinc-100 border-white/30 hover:border-[#e58a59] hover:text-white'
              }`}>
                {i + 1}
              </span>
            </button>

            <AnimatePresence>
              {isActive && (
                <motion.div
                  key={`callout-${a.id}`}
                  initial={{ opacity: 0, x: pinSide === 'left' ? 6 : -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: pinSide === 'left' ? 6 : -6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute z-20 -translate-y-1/2 pointer-events-none"
                  style={{
                    ...pinCalloutStyle,
                    top: `${a.y}%`,
                    maxWidth: `${fs ? Math.min(360, calloutMaxWidth + 80) : calloutMaxWidth}px`,
                  }}
                  data-testid={`anno-callout-${a.id}`}
                >
                  <div className="bg-black/85 backdrop-blur-md border border-[#e58a59]/40 rounded-lg p-3.5 shadow-xl">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] font-semibold">
                        {String(i + 1).padStart(2, '0')} · {a.label}
                        {a.inactive && <span className="ml-2 text-zinc-500">· inactive</span>}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-200 leading-relaxed">{a.note}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="rounded-xl overflow-hidden border border-white/10 bg-[#1a1a1c] glow-card" data-testid={testId}>
        {canvas}
        <div className="p-6">
          <h4 className="font-display text-xl text-white font-bold mb-2">{title}</h4>
          {intro && <p className="text-zinc-400 text-sm leading-relaxed">{intro}</p>}
          <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-zinc-500 md:hidden">
            Tap <span className="text-[#e58a59]">Expand</span> for a bigger, readable view
          </p>
          {activeAnno && (
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#e58a59]">
              Reading: {activeAnno.label}
              {activeAnno.inactive && <span className="text-zinc-500"> · inactive</span>}
            </p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {fs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
            onClick={() => setFs(false)}
            data-testid={`${testId}-fs-overlay`}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl mx-auto rounded-xl overflow-hidden border border-white/10 bg-[#0e0e10]"
              style={{ aspectRatio: '16 / 10' }}
            >
              {canvas}
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/70 border border-white/10 text-[10px] uppercase tracking-[0.22em] text-zinc-200 pointer-events-none">
              Tap a pin · tap outside or ESC to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


function UIScreen({ image, label, title, blurb, items, testId }) {
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-[#1a1a1c] glow-card" data-testid={testId}>
      <div className="image-mask aspect-[16/10] overflow-hidden bg-[#0e0e10] relative">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#e58a59]">{label}</div>
      </div>
      <div className="p-6">
        <h4 className="font-display text-xl text-white font-bold mb-2">{title}</h4>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">{blurb}</p>
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span key={it} className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 px-2.5 py-1 rounded-full border border-white/10 bg-[#121214]">{it}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function UIContent() {
  return (
    <div className="pt-4 space-y-5">
      <AnnotatedScreen
        image={UI_HOME}
        label="Main Menu"
        title="Home — Campaign Hub"
        intro="Diegetic main menu rendered live inside the game world — overlooking a human settlement. Built in UMG so the camera framing makes every menu open feel like stepping into the campaign."
        annotations={HOME_ANNOTATIONS}
        anchorX={22}
        testId="rts-ui-home"
      />
      <AnnotatedScreen
        image={UI_BATTLE}
        label="Battle Mode"
        title="Battle for Existence"
        intro="Local play (Create / Join) is live in the current build; the three online modes are reserved as future slots — visible on the menu so the roadmap is part of the design."
        annotations={BATTLE_ANNOTATIONS}
        anchorX={78}
        calloutSide="left"
        testId="rts-ui-battle"
      />
      <p className="text-zinc-500 text-xs italic">Working in-engine build label: <span className="text-zinc-300">AnnihilationAge</span> — shipping title: <span className="text-[#e58a59]">The Crimson Dirge</span>.</p>
    </div>
  );
}

function MapCard({ image, title, role, meta, blurb, testId }) {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-[#1a1a1c]" data-testid={testId}>
      <div className="aspect-[4/3] overflow-hidden bg-[#0e0e10]"><img src={image} alt={title} className="w-full h-full object-cover" /></div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#e58a59] mb-2">{role}</div>
        <h4 className="font-display text-xl text-white font-bold mb-2">{title}</h4>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">{blurb}</p>
        <div className="grid grid-cols-3 gap-3 border-t border-white/10 pt-3">
          {meta.map((m) => (
            <div key={m.k}>
              <div className="text-sm text-white font-semibold">{m.v}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{m.k}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="pointer-events-auto px-4 py-3 rounded-lg backdrop-blur-md bg-black/55 border border-white/15 min-w-[170px]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-300"><Icon size={12} className="text-[#e58a59]" />{label}</div>
      <div className="mt-1"><span className="font-display text-2xl text-white font-bold">{value}</span></div>
      <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>
    </div>
  );
}
